import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'

import { includeInactiveParameter, readSearchState, writeSearchState, type SearchState } from './search-state'

const searchParameters: ReadonlySet<string> = new Set(['q', 'filter', 'sort', 'columns', 'tz', includeInactiveParameter])

/**
 * The search state in the page's URL, and a function that changes it. A change replaces the
 * history entry, so typing does not fill the back button; parameters a search does not own stay.
 */
export function useSearchState(): readonly [SearchState, (change: (state: SearchState) => SearchState) => void] {
  const [params, setParams] = useSearchParams()
  const state = useMemo(() => readSearchState(params), [params])

  const update = useCallback(
    (change: (state: SearchState) => SearchState) => {
      setParams(
        (current) => {
          const next = writeSearchState(change(readSearchState(current)))
          for (const [name, value] of current) {
            if (!searchParameters.has(name)) {
              next.append(name, value)
            }
          }
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  return [state, update] as const
}
